import { useState, useEffect, useContext } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { EncryptionContext } from "./EncryptionContext.jsx";
import { decryptText, getEncryptionSecret } from "../utils";

function PreviousEntriesTab({ user, db, selectedCycle, setDecryptionError, prompts }) {
  const { passphrase } = useContext(EncryptionContext);
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [copyConfirmation, setCopyConfirmation] = useState("");

  // Fetch journal entries
  useEffect(() => {
    const fetchEntries = async () => {
      if (!user || !selectedCycle) {
        console.log("Missing required data for fetching entries:", { user, selectedCycle });
        return;
      }
      setLoading(true);
      try {
        const cyclePath = `users/${user.uid}/cycles/${selectedCycle.id}`;
        const entriesQuery = query(
          collection(db, `${cyclePath}/journalEntries`),
          orderBy("date", "desc")
        );
        console.log("Fetching entries for cycle:", selectedCycle.id);
        const entriesSnapshot = await getDocs(entriesQuery);
        const decryptedEntries = await Promise.all(
          entriesSnapshot.docs.map(async (doc) => ({
            id: doc.id,
            ...doc.data(),
            text: await decryptText(doc.data().text, user, getEncryptionSecret(user, passphrase), setDecryptionError),
            showFull: false,
          }))
        );
        setEntries(decryptedEntries);
        setError("");
        setDecryptionError("");
      } catch (error) {
        console.error("Error fetching entries:", error.message, error.code, error);
        setError("Error fetching journal entries. Please try again.");
        setDecryptionError("Error fetching journal entries. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchEntries();
  }, [user, selectedCycle, passphrase, db, setDecryptionError]);

  // Handle text search
  const filteredEntries = entries.filter((entry) =>
    entry.text.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle bulk selection
  const toggleEntrySelection = (id) => {
    setSelectedEntries((prev) =>
      prev.includes(id)
        ? prev.filter((entryId) => entryId !== id)
        : [...prev, id]
    );
  };

  const selectAllEntries = () => {
    setSelectedEntries(filteredEntries.map((entry) => entry.id));
  };

  const deselectAllEntries = () => {
    setSelectedEntries([]);
  };

  // Handle bulk copy
  const handleBulkCopy = async () => {
    if (!selectedEntries.length) return;
    setLoading(true);
    try {
      const selectedTexts = entries
        .filter((entry) => selectedEntries.includes(entry.id))
        .map((entry) => stripHtml(entry.text))
        .join("\n\n---\n\n");
      await navigator.clipboard.writeText(selectedTexts);
      setCopyConfirmation("Selected entries copied to clipboard!");
      setTimeout(() => setCopyConfirmation(""), 3000);
      setError("");
      setDecryptionError("");
    } catch (error) {
      console.error("Error copying entries:", error.message, error.code);
      setError("Error copying selected entries. Please try again.");
      setDecryptionError("Error copying selected entries. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Toggle show more/less
  const toggleShowFull = (id) => {
    setEntries((prev) =>
      prev.map((entry) =>
        entry.id === id ? { ...entry, showFull: !entry.showFull } : entry
      )
    );
  };

  // Strip HTML tags for preview
  const stripHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  return (
    <div className="card animate-fade-in w-full mt-0">
      <h2 className="text-2xl font-semibold text-text-light dark:text-text-dark mb-4">Previous Entries</h2>
      {error && (
        <div className="mb-4 p-2 bg-red-100 dark:bg-red-900 text-error rounded">
          {error}
        </div>
      )}
      {copyConfirmation && (
        <div className="mb-4 p-2 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded">
          {copyConfirmation}
        </div>
      )}
      {loading && (
        <div className="flex justify-center items-center my-4">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" aria-label="Loading entries" />
        </div>
      )}
      {!loading && (
        <>
          <div className="mb-6">
            <label className="block text-text-light dark:text-text-dark font-medium mb-1">
              Search Entries
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input w-full"
              placeholder="Search your journal entries..."
              aria-label="Search journal entries"
            />
          </div>
          <div className="mb-6 flex flex-col sm:flex-row gap-3">
            <button
              onClick={selectAllEntries}
              className="btn-primary w-full sm:w-auto"
              disabled={loading || filteredEntries.length === 0}
              aria-label="Select all entries"
            >
              Select All
            </button>
            <button
              onClick={deselectAllEntries}
              className="btn-secondary w-full sm:w-auto"
              disabled={loading || selectedEntries.length === 0}
              aria-label="Deselect all entries"
            >
              Deselect All
            </button>
            <button
              onClick={handleBulkCopy}
              className="btn-secondary w-full sm:w-auto text-primary"
              disabled={loading || selectedEntries.length === 0}
              aria-label="Copy selected entries"
            >
              Copy Selected
            </button>
          </div>
          {filteredEntries.length === 0 ? (
            <p className="text-neutral text-center">
              No journal entries found. Try adjusting your search or add a new entry!
            </p>
          ) : (
            <ul className="space-y-4">
              {filteredEntries.map((entry) => (
                <li
                  key={entry.id}
                  className="p-4 rounded-lg border border-neutral/20 dark:border-neutral/40 flex flex-col"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <input
                      type="checkbox"
                      checked={selectedEntries.includes(entry.id)}
                      onChange={() => toggleEntrySelection(entry.id)}
                      className="h-5 w-5 text-accent rounded focus:ring-accent"
                      disabled={loading}
                      aria-label={`Select entry ${entry.id}`}
                    />
                    <p className="text-text-light dark:text-text-dark">
                      {entry.showFull ? (
                        <div
                          dangerouslySetInnerHTML={{ __html: entry.text }}
                        />
                      ) : (
                        stripHtml(entry.text).substring(0, 300) +
                        (stripHtml(entry.text).length > 300 ? "..." : "")
                      )}
                    </p>
                  </div>
                  {stripHtml(entry.text).length > 300 && (
                    <button
                      onClick={() => toggleShowFull(entry.id)}
                      className="text-secondary hover:underline text-sm self-start"
                      disabled={loading}
                      aria-label={entry.showFull ? "Show less" : "Show more"}
                    >
                      {entry.showFull ? "Show Less" : "Show More"}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

export default PreviousEntriesTab;